import { inject, observer } from 'mobx-react';
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { AppContextType } from '@byted-cg/linkchat-plugin';

import { onKeyboardUpOrDown } from '../utils/keyboard';
import { usePanel } from './hooks/usePanel';
import { IInputWrapperRef } from './InputWrapper';

type Panel = {
  name: string;
  panel: React.ReactElement;
};

interface PanelWrapperProps {
  appContext?: AppContextType;
  inputRef?: React.RefObject<IInputWrapperRef>;
  panels: Panel[];
}

export interface IPanelWrapperRef {
  toggle: (panel: false | string) => void;
  getCur: () => string;
}

// Set keyboard default height
const DEFAULT_KEYBOARD_HEIGHT = '250px';

const PanelWrapper = React.forwardRef<IPanelWrapperRef, PanelWrapperProps>(
  ({ inputRef, panels, appContext }, ref) => {
    // Set the height of toolpanel or emojipanel
    const [keyboardHeight, setKeyboardHeight] = useState(
      DEFAULT_KEYBOARD_HEIGHT,
    );

    // Currently open panel
    const [currentPanel, _setCurrentPanel] = usePanel();

    // Sync currentPanel
    const currentPanelRef = useRef(currentPanel);
    const setCurrentPanel = (panel: string) => {
      _setCurrentPanel(panel);
      currentPanelRef.current = panel;
    };

    // Switch logic
    const handleToggle = useCallback((panel: false | string) => {
      // Close the panel
      if (panel === false) {
        setCurrentPanel('');
        appContext?.$refs?.$input?.current?.blur();
        return;
      }
      // Open keyboard
      if (panel === 'keyboard') {
        appContext?.$refs?.$input?.current?.focus();
        return;
      }
      if (currentPanelRef.current === 'keyboard') {
        onKeyboardUpOrDown({
          onKeyboardDown: () => {
            setCurrentPanel(panel);
          },
        });
      } else {
        setCurrentPanel(panel);
      }
    }, []);

    useImperativeHandle(
      ref,
      (): IPanelWrapperRef => ({
        toggle: handleToggle,
        getCur: () => currentPanel,
      }),
    );

    // Open toolpanel or emojipanel and scroll to the bottom
    useEffect(() => {
      // Page scrolls to the bottom
      window.scrollTo(0, document.body.scrollHeight);
      // Message scrolls to the bottom
      appContext?.$refs?.$scroller?.current?.scrollToBottom(false);
    }, [currentPanel]);

    // Monitor keyboard eject/retract events
    useEffect(() => {
      if (inputRef?.current?.getEle && inputRef?.current?.getEle()) {
        const cancel = onKeyboardUpOrDown({
          input: inputRef.current.getEle(),
          onKeyboardUp: (height) => {
            // Set panel height
            // setKeyboardHeight(prevValue => {
            //   const newValue = `${height}px`;
            //   if (newValue === '0px') return prevValue;
            //   return newValue;
            // });
            // Turn on the keyboard
            setCurrentPanel('keyboard');
          },
          onKeyboardDown: (height) => {
            // Set panel height
            // setKeyboardHeight(prevValue => {
            //   const newValue = `${height}px`;
            //   if (newValue === '0px') return prevValue;
            //   return newValue;
            // });
            if (currentPanelRef.current === 'keyboard') {
              setCurrentPanel('');
            }
          },
        });
        return () => {
          typeof cancel === 'function' && cancel();
        };
      }
    }, [inputRef?.current]);

    // Calculate the panel that currently needs to be displayed
    const curPanel = useMemo(() => {
      return panels.find(({ name }) => name === currentPanel)?.panel;
    }, [currentPanel, panels]);

    // Determine whether to display the panel
    const display = useMemo(() => {
      // Show custom panel if currently selected panel exists
      if (panels.some(({ name }) => name === currentPanel)) return 'block';
      // Other cases hide panel
      return 'none';
    }, [currentPanel, panels]);
    return (
      <div
        style={{
          height: keyboardHeight,
          display: display,
          overflow: 'auto',
          paddingBottom: 'var(--safe-bottom)',
          backgroundColor: 'var(--tool-panel-bg)',
        }}
      >
        {panels.map(({ name, panel }) =>
          React.cloneElement(panel, {
            style:
              name === currentPanel
                ? {}
                : {
                    display: 'none',
                  },
          }),
        )}
      </div>
    );
  },
);

export default inject(({ appContext }) => ({
  appContext,
}))(observer(PanelWrapper));
